import { createContext, useCallback, useMemo, useState } from 'react';

import { AnyObject } from '@typings';
import { isObject, noop } from '@utils';

function flattenObject(
  obj: AnyObject,
  parentKey = '',
  result: AnyObject = {},
): AnyObject {
  Object.entries(obj).forEach(([key, value]) => {
    const path = parentKey ? `${parentKey}.${key}` : key;

    if (isObject(value) && value !== null) {
      result[key] = value;

      flattenObject(value, path, result);
    } else {
      result[path] = value;
    }
  });

  return result;
}

type Context = {
  importedConfig: AnyObject;
  onConfigApply: (data: AnyObject) => void;
};

export const SchemaContext = createContext<Context>({
  importedConfig: {},
  onConfigApply: noop,
});

type Props = {
  reset?: (data: AnyObject) => void;
  children: React.ReactNode;
};

export const SchemaProvider: React.FC<Props> = ({ reset, children }) => {
  const [config, setConfig] = useState<AnyObject>({});

  const handleConfigApply = useCallback(
    (data: AnyObject) => {
      reset?.(data);
      setConfig(flattenObject(data));
    },
    [reset],
  );

  const value: Context = useMemo(
    () => ({
      importedConfig: config,
      onConfigApply: handleConfigApply,
    }),
    [config, handleConfigApply],
  );

  return (
    <SchemaContext.Provider value={value}>{children}</SchemaContext.Provider>
  );
};
